import React, { useState, useEffect, useRef } from 'react'
import { apiClient } from '@/utils/api'
import { useAuth } from '@/hooks/useAuth'
import { useFamilyFeatures } from '@/hooks/useFamilyFeatures'
import { setDashboardVariant } from '@/utils/dashboardVariant'
import { 
  CheckCircle, Clock, AlertCircle, Users, Calendar, Plus,
  TrendingUp, Archive, ChevronRight, Eye, Edit2, Trash2,
  X, Check, Star, UserCheck, AlertTriangle, ChevronDown,
  RotateCcw, Award, Filter, Search, RefreshCw, CheckSquare,
  History, PlusCircle, Shuffle, List, Grid, Save, ArrowUpDown,
  Gift, MessageSquare, CreditCard, ShoppingCart, Bell, Shield, LogOut, Settings,
  Menu, CalendarDays, CheckCircle2, GraduationCap
} from 'lucide-react'
import { format, parseISO, differenceInDays, isToday, isTomorrow, isPast, startOfWeek, endOfWeek, startOfDay, isWithinInterval, subDays, endOfDay } from 'date-fns'
import { isOverdue } from '@/utils/dateUtils'
import { getTodayDateString, getTomorrowDateString, extractDateInTimezoneSync, parseDueDate } from '@/utils/timezoneUtils'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RewardsEditorFixed } from './RewardsEditorFixed'
import { SchoolEvents } from './SchoolEvents'
import { AvailabilityApprovals } from './AvailabilityApprovals'
import ChoreAssignmentEditModal from './ChoreAssignmentEditModal'
import { EnhancedDeductionsManager } from './EnhancedDeductionsManager'
import RecipeList from './recipes/RecipeList'
import { ShoppingListGenerator } from './ShoppingListGenerator'
import { CalendarSelector } from './CalendarSelector'
import { SchoolCalendarView } from './SchoolCalendarView'
import FamilyScheduleFixed from './FamilyScheduleFixed'
import ParentSchoolManagement from '@/pages/ParentSchoolManagement'
import { NotificationCenter } from './NotificationCenter'
import NotificationSettings from './NotificationSettings'
import { RecipeImporter } from './recipes/RecipeImporter'
import { EnhancedMealPlanEditor } from './EnhancedMealPlanEditor'
import EnhancedChoreVerification from './EnhancedChoreVerification'
import PointsOverview from './PointsOverview'
import RedemptionQueue from './RedemptionQueue'
import WeeklySummary from './WeeklySummary'
import PerformanceTrends from './PerformanceTrends'
import { FamilyEvents } from './FamilyEvents'
import { PriorityBar } from './PriorityBar'
import { GreenlightConverter } from './GreenlightConverter'
import { QuickActionsWidget } from './QuickActionsWidget'
import { AssignmentHistory } from './AssignmentHistory'
import { SmartInsightsPanel } from './SmartInsightsPanel'
import { APP_VERSION } from '@/version'
import { toConfiguredTimezone } from '@/utils/timezoneUtils'
import { CalendarDashboardCore } from './calendar/CalendarDashboardCore'
import { UnifiedCalendarDashboard } from './calendar/UnifiedCalendarDashboard'
import { TodaysSummary } from './TodaysSummary'
import { LayoutGrid, CalendarDays as CalendarIcon } from 'lucide-react'
import { useDemoMode } from './DemoModeBanner'
import { PWAInstallPrompt, PWAInstallButton } from './PWAInstallPrompt'

interface Assignment {
  id: string
  chore_id: string
  assigned_to: string
  assigned_to_name?: string
  assigned_user?: {
    display_name: string
    username: string
    color_theme: string
  }
  status: string
  points_offered: number
  points_earned?: number
  due_date: string
  created_at: string
  completed_at?: string
  verified_at?: string
  verified_by?: string
  verification_notes?: string
  completion_notes?: string
  completion_photo_url?: string
  chore: {
    id: string
    name: string
    description: string
    category: string
    difficulty: string
    estimated_minutes: number
    requires_verification: boolean
    base_points: number
    icon?: string
  }
}

interface Chore {
  id: string
  name: string
  description: string
  category: string
  base_points: number
  estimated_minutes: number
  difficulty: string
  requires_verification: boolean
  is_active: boolean
  rotation_group_id?: string
}

interface Child {
  id: string
  display_name: string
  username: string
  total_points: number
  available_points?: number
  is_present?: boolean
  color_theme?: string
}

interface RotationGroup {
  id: string
  name: string
  members: string[]
  chores: string[]
  rotation_days: string
  current_index: number
}

interface QuickStats {
  todayAssigned: number
  pendingVerification: number
  overdue: number
  openChores: number
  completedToday: number
  verifiedToday: number
  weeklyCompletion: number
}

type ViewType = 'overview' | 'assign' | 'assignments' | 'history' | 'requests' | 'deductions' | 'recipes' | 'shopping' | 'calendars' | 'family-events' | 'notifications' | 'recipe-import' | 'meals' | 'redemption-queue' | 'weekly-summary' | 'performance-trends' | 'greenlight-converter'

type AssignmentTab = 'active' | 'verify' | 'overdue'

export const ParentDashboard: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { isDemoMode } = useDemoMode()
  const { isFeatureEnabled } = useFamilyFeatures()

  // Greenlight is a per-family feature toggle (family_feature_toggles.greenlight)
  const greenlightEnabled = isFeatureEnabled('greenlight')

  const [activeView, setActiveView] = useState<ViewType>('overview')
  const [activeAssignmentTab, setActiveAssignmentTab] = useState<AssignmentTab>('active')
  const [assignments, setAssignments] = useState<Assignment[]>([])
  const [chores, setChores] = useState<Chore[]>([])
  const [children, setChildren] = useState<Child[]>([])
  const [rotationGroups, setRotationGroups] = useState<RotationGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<QuickStats>({
    todayAssigned: 0,
    pendingVerification: 0,
    overdue: 0,
    openChores: 0,
    completedToday: 0,
    verifiedToday: 0,
    weeklyCompletion: 0
  })
  const [pendingRedemptions, setPendingRedemptions] = useState(0)
  const [pendingAvailability, setPendingAvailability] = useState(0)

  // Filters and UI state
  const [selectedChild, setSelectedChild] = useState<string>('all')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [groupByCategory, setGroupByCategory] = useState(true)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [dateFilter, setDateFilter] = useState<'today' | '2days' | 'week' | 'month' | 'all'>('all')
  const [historyDateFilter, setHistoryDateFilter] = useState<'today' | '2days' | 'week' | 'month' | 'all'>('week')
  const [manageSortBy, setManageSortBy] = useState<'due_date' | 'created_date' | 'points' | 'user' | 'status'>('due_date')
  const [manageSortOrder, setManageSortOrder] = useState<'asc' | 'desc'>('asc')
  const [manageUserFilter, setManageUserFilter] = useState<string>('all')
  
  // Chore Library filters
  const [choreSearchTerm, setChoreSearchTerm] = useState('')
  const [choreCategoryFilter, setChoreCategoryFilter] = useState<string>('all')
  const [choreDifficultyFilter, setChoreDifficultyFilter] = useState<string>('all')
  const [choreSortBy, setChoreSortBy] = useState<'name' | 'points' | 'time' | 'difficulty' | 'category'>('category')
  const [choreViewMode, setChoreViewMode] = useState<'grid' | 'list' | 'grouped'>('grouped')
  const [selectedChoreForAssign, setSelectedChoreForAssign] = useState<Chore | null>(null)
  
  // Create & Assign filters
  const [assignSearchTerm, setAssignSearchTerm] = useState('')
  const [assignCategoryFilter, setAssignCategoryFilter] = useState('')
  const [assignSelectedChild, setAssignSelectedChild] = useState('')
  const [assignDueDate, setAssignDueDate] = useState('')

  // Initialize assignDueDate with timezone-aware date
  useEffect(() => {
    getTodayDateString().then(setAssignDueDate)
  }, [])
  
  // New chore creation state
  const [showNewChoreForm, setShowNewChoreForm] = useState(false)
  const [newChore, setNewChore] = useState({
    name: '',
    description: '',
    category: 'cleaning',
    base_points: 10,
    estimated_minutes: 15,
    difficulty: 'easy',
    requires_verification: true,
    make_open: false,
    assigned_to: ''
  })
  
  // Edit modal state
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  
  // Calendar state
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([
    'district-cobb', 
    'district-paulding',
    'family-schedule',
    'family-holidays'
  ])
  const [calendarView, setCalendarView] = useState<'day' | '3day' | 'week' | 'month' | 'agenda'>('week')
  const [selectedKid, setSelectedKid] = useState<string>('all')
  
  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Dashboard mode state
  const [dashboardMode, setDashboardMode] = useState<'classic' | 'calendar'>('calendar')
  const [presenceData, setPresenceData] = useState<any[]>([])
  const [schoolEvents, setSchoolEvents] = useState<any[]>([])

  // Timezone-aware date strings
  const [todayDateStr, setTodayDateStr] = useState<string>('')
  const [tomorrowDateStr, setTomorrowDateStr] = useState<string>('')

  // Initialize timezone-aware dates
  useEffect(() => {
    const initTimezone = async () => {
      const today = await getTodayDateString()
      const tomorrow = await getTomorrowDateString()
      setTodayDateStr(today)
      setTomorrowDateStr(tomorrow)
    }
    initTimezone()
  }, [])

  useEffect(() => {
    // Load data immediately on mount
    loadData()

    // Set up refresh interval
    const interval = setInterval(() => loadData(true), 30000) // Smart refresh every 30 seconds

    return () => {
      clearInterval(interval)
    }
  }, [])

  // Refetch data when switching to overview to ensure fresh data
  const prevViewRef = useRef<ViewType | null>(null)
  useEffect(() => {
    // Reload if we're switching TO overview from another view (including initial load)
    if (activeView === 'overview' && prevViewRef.current !== null && prevViewRef.current !== 'overview') {
      loadData(false) // Force full reload without preserving UI state
    }
    prevViewRef.current = activeView
  }, [activeView])

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Auto-expand categories with assignments when switching to assignments view
  useEffect(() => {
    if (activeView === 'assignments' && groupByCategory) {
      // Get all categories that have assignments
      const categoriesWithAssignments = new Set<string>()
      getFilteredAssignments().forEach(assignment => {
        if (assignment.chore.category) {
          categoriesWithAssignments.add(assignment.chore.category)
        }
      })
      // Auto-expand all categories with assignments
      if (categoriesWithAssignments.size > 0) {
        setExpandedCategories(categoriesWithAssignments)
      }
    }
  }, [activeView, assignments, dateFilter])

  // Get view title based on active view
  const getViewTitle = () => {
    const titles: Record<ViewType, string> = {
      overview: 'Dashboard Overview',
      assign: 'Assign Tasks',
      assignments: 'Assignments',
      history: 'Assignment History',
      rotations: 'Chore Rotations',
      rewards: 'Rewards Store',
      requests: 'Kid Requests',
      deductions: 'Point Deductions',
      recipes: 'Recipe Library',
      shopping: 'Shopping List',
      calendars: 'Calendars & Events',
      'school-management': 'School & Transport',
      notifications: 'Notifications',
      'recipe-import': 'Import Recipe',
      meals: 'Meal Planning',
      'redemption-queue': 'Redemption Queue',
      'weekly-summary': 'Weekly Summary',
      'performance-trends': 'Performance Trends',
      'greenlight-converter': 'Greenlight Points Converter'
    }
    return titles[activeView] || 'Dashboard'
  }

  const loadData = async (preserveUIState = false, retryCount = 0) => {
    try {
      // Only show loading spinner on initial load
      if (!preserveUIState) {
        setLoading(true)
      }
      
      // Check if we have auth token - give it up to 3 retries
      const token = localStorage.getItem('auth_token')
      if (!token && retryCount < 3) {
        // Wait a bit for auth to be ready
        await new Promise(resolve => setTimeout(resolve, 300))
        return loadData(preserveUIState, retryCount + 1)
      }
      
      // If still no token after retries, at least show the UI
      if (!token) {
        setLoading(false)
        console.warn('No auth token available after retries')
        return
      }
      
      // Load all data in parallel with individual error handling
      // Fetch all assignments (active, completed, and verified)
      const [activeAssignments, completedAssignments, childrenData, choresList, rotations, redemptions, availabilityRequests, familySchedule, schoolEventsData] = await Promise.all([
        apiClient.getAssignments(undefined, undefined, 'all', 500).catch((err) => {
          console.error('Failed to load active assignments:', err)
          return []
        }),
        apiClient.getAssignments('completed', undefined, 'all', 100).catch((err) => {
          console.error('Failed to load completed assignments:', err)
          return []
        }),
        apiClient.get('/api/parent/children').catch((err) => {
          console.error('Failed to load children:', err)
          return []
        }),
        apiClient.get('/api/chores').catch((err) => {
          console.error('Failed to load chores:', err)
          return []
        }),
        apiClient.get('/api/rotation-groups').catch(() => []),
        apiClient.get('/api/rewards/redemptions').catch(() => []),
        apiClient.get('/api/availability/coverage-requests').catch(() => []),
        apiClient.get('/api/kiosk/family-schedule').catch((err) => {
          console.error('Failed to load family schedule:', err)
          return []
        }),
        // Only fetch schools data if the school_integration feature is enabled
        isFeatureEnabled('school_integration')
          ? apiClient.get('/api/schools/events').catch((err) => {
              // Only log errors that are not 403 (Forbidden) - 403 is expected for families without school integration
              if (err.response?.status !== 403) {
                console.error('Failed to load school events:', err)
              }
              return []
            })
          : Promise.resolve([])
      ])

      // Deduplicate assignments by ID before merging
      const allAssignments = [...activeAssignments, ...completedAssignments]
      const uniqueAssignments = Array.from(
        new Map(allAssignments.map(a => [a.id, a])).values()
      )

      // Process assignments to add child names
      // Ensure all data is in array format (defensive check for API format differences)
      const assignmentsArray = Array.isArray(uniqueAssignments) ? uniqueAssignments :
                                (uniqueAssignments?.assignments && Array.isArray(uniqueAssignments.assignments)) ? uniqueAssignments.assignments : []
      const childrenArray = Array.isArray(childrenData) ? childrenData :
                            (childrenData?.children && Array.isArray(childrenData.children)) ? childrenData.children : []
      const processedAssignments = assignmentsArray.map((a: Assignment) => {
        const child = childrenArray.find((c: any) => c.id === a.assigned_to)
        return {
          ...a,
          assigned_to_name: (a.status === 'open' || !a.assigned_to) ? 'Open' : (child?.display_name || a.assigned_user?.display_name || 'Unassigned')
        }
      })


      setAssignments(processedAssignments)
      setChildren(childrenArray)
      // Ensure choresList is an array (defensive check for API format differences)
      const choresArray = Array.isArray(choresList) ? choresList :
                          (choresList?.chores && Array.isArray(choresList.chores)) ? choresList.chores : []
      setChores(choresArray)
      // Rotation groups loaded successfully
      // Ensure rotations is an array and has proper structure
      const validRotations = Array.isArray(rotations) ? rotations :
                             (rotations?.rotation_groups && Array.isArray(rotations.rotation_groups)) ? rotations.rotation_groups : []
      setRotationGroups(validRotations)

      // Calculate stats
      const today = new Date()
      const weekStart = startOfWeek(today)
      const weekEnd = endOfWeek(today)

      const pendingVerifications = processedAssignments.filter((a: Assignment) =>
        (a.status === 'completed' || a.status === 'pending_verification') &&
        !a.verified_at
      )

      const stats: QuickStats = {
        todayAssigned: processedAssignments.filter((a: Assignment) => {
          // Match the exact same logic as manage view filter for 'today'
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          // due_date is a date string (YYYY-MM-DD), not a datetime
          // Use T12:00:00 to avoid timezone issues
          const assignmentDate = a.due_date ? parseDueDate(a.due_date) : new Date(a.created_at)
          assignmentDate.setHours(0, 0, 0, 0)
          const tomorrow = new Date(today)
          tomorrow.setDate(tomorrow.getDate() + 1)
          return assignmentDate >= today && assignmentDate < tomorrow
        }).length,
        pendingVerification: pendingVerifications.length,
        overdue: processedAssignments.filter((a: Assignment) => {
          if (!a.due_date || ['completed', 'verified'].includes(a.status)) return false
          return isOverdue(a.due_date)
        }).length,
        openChores: processedAssignments.filter((a: Assignment) => 
          a.status === 'open'
        ).length,
        completedToday: processedAssignments.filter((a: Assignment) => {
          if (!a.completed_at || !todayDateStr) return false
          const completedDateStr = extractDateInTimezoneSync(a.completed_at)
          return completedDateStr === todayDateStr
        }).length,
        verifiedToday: processedAssignments.filter((a: Assignment) => {
          if (!a.verified_at || !todayDateStr) return false
          const verifiedDateStr = extractDateInTimezoneSync(a.verified_at)
          return verifiedDateStr === todayDateStr
        }).length,
        weeklyCompletion: Math.round(
          (processedAssignments.filter((a: Assignment) => {
            const dueDate = a.due_date ? parseISO(a.due_date) : null
            return dueDate && dueDate >= weekStart && dueDate <= weekEnd && 
                   ['completed', 'verified'].includes(a.status)
          }).length / 
          Math.max(1, processedAssignments.filter((a: Assignment) => {
            const dueDate = a.due_date ? parseISO(a.due_date) : null
            return dueDate && dueDate >= weekStart && dueDate <= weekEnd
          }).length)) * 100
        )
      }
      
      setStats(stats)

      // Set pending counts for Priority Bar (with defensive array checks)
      const redemptionsArray = Array.isArray(redemptions) ? redemptions :
                               (redemptions?.redemptions && Array.isArray(redemptions.redemptions)) ? redemptions.redemptions : []
      setPendingRedemptions(redemptionsArray.filter((r: any) => r.status === 'pending').length)

      // Coverage requests that need attention (pending status or within review period)
      const availabilityArray = Array.isArray(availabilityRequests) ? availabilityRequests :
                                (availabilityRequests?.requests && Array.isArray(availabilityRequests.requests)) ? availabilityRequests.requests : []
      setPendingAvailability(availabilityArray.filter((r: any) =>
        r.status === 'pending' || r.status === 'open'
      ).length)

      // Set presence data for calendar view
      const scheduleArray = Array.isArray(familySchedule) ? familySchedule :
                           (familySchedule?.schedule && Array.isArray(familySchedule.schedule)) ? familySchedule.schedule : []
      setPresenceData(scheduleArray)

      // Set school events for calendar view
      const eventsArray = Array.isArray(schoolEventsData) ? schoolEventsData : []
      setSchoolEvents(eventsArray)
    } catch (error) {
      console.error('Failed to load data:', error)
      // Only show error toast on initial load
      if (!preserveUIState) {
        toast.error('Failed to load dashboard data')
      }
    } finally {
      // Only clear loading on initial load
      if (!preserveUIState) {
        setLoading(false)
      }
    }
  }

  const handleCreateChore = async () => {
    try {
      // Create the chore
      const choreResponse = await apiClient.post('/api/chores', {
        name: newChore.name,
        description: newChore.description,
        category: newChore.category,
        base_points: newChore.base_points,
        estimated_minutes: newChore.estimated_minutes,
        difficulty: newChore.difficulty,
        requires_verification: newChore.requires_verification,
        is_active: true
      })

      // Create assignment (either open or assigned)
      await apiClient.post('/api/assignments/', {
        chore_id: choreResponse.id,
        assigned_to: newChore.assigned_to || null,
        status: newChore.assigned_to ? 'pending' : 'open',
        points_offered: newChore.base_points,
        due_date: extractDateInTimezoneSync(new Date().toISOString())
      })

      toast.success(newChore.make_open ? 'Open chore created for bidding!' : 'Chore assigned!')
      setShowNewChoreForm(false)
      setNewChore({
        name: '',
        description: '',
        category: 'cleaning',
        base_points: 10,
        estimated_minutes: 15,
        difficulty: 'easy',
        requires_verification: true,
        make_open: false,
        assigned_to: ''
      })
      loadData()
    } catch (error) {
      toast.error('Failed to create chore')
    }
  }

  const handleVerifyAssignment = async (assignmentId: string, approved: boolean, notes?: string) => {
    try {
      const assignment = assignments.find(a => a.id === assignmentId)
      await apiClient.post(`/api/assignments/${assignmentId}/verify`, {
        approved: approved,
        points_awarded: approved ? assignment?.points_offered : 0,
        verification_notes: notes || (approved ? 'Approved' : 'Needs to be redone')
      })
      toast.success(approved ? 'Chore verified!' : 'Chore rejected')
      loadData()
    } catch (error) {
      toast.error('Failed to verify chore')
    }
  }

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to delete this assignment?')) return

    try {
      await apiClient.delete(`/api/assignments/${assignmentId}`)
      toast.success('Assignment deleted')
      loadData()
    } catch (error) {
      toast.error('Failed to delete assignment')
    }
  }

  const handleReassignChore = async (assignmentId: string, newChildId: string) => {
    try {
      const assignment = assignments.find(a => a.id === assignmentId)
      const newChild = children.find(c => c.id === newChildId)

      if (!assignment || !newChild) {
        toast.error('Invalid assignment or child')
        return
      }

      await apiClient.put(`/api/assignments/${assignmentId}`, {
        assigned_to: newChildId
      })

      toast.success(`Chore reassigned to ${newChild.display_name}`)
      loadData()
    } catch (error) {
      toast.error('Failed to reassign chore')
    }
  }

  const handleUnverifyAssignment = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to undo this verification? Points will be deducted.')) return
    
    try {
      await apiClient.post(`/api/assignments/${assignmentId}/unverify`)
      toast.success('Verification undone, points deducted')
      loadData()
    } catch (error) {
      toast.error('Failed to undo verification')
    }
  }

  const handleCompleteAssignment = async (assignmentId: string, childName: string) => {
    if (!confirm(`Mark this chore as completed by ${childName}?`)) return

    try {
      await apiClient.post(`/api/assignments/${assignmentId}/parent-complete`, {
        notes: 'Completed by parent on behalf of child'
      })
      toast.success('Chore marked as completed and verified!')
      loadData()
    } catch (error) {
      toast.error('Failed to complete chore')
    }
  }

  const handleEditAssignment = (assignment: Assignment) => {
    setEditingAssignment(assignment)
    setShowEditModal(true)
  }
  
  const handleQuickReschedule = async (assignmentId: string) => {
    try {
      // Reschedule to tomorrow
      const newDueDate = await getTomorrowDateString()

      await apiClient.put(`/api/assignments/${assignmentId}`, {
        due_date: newDueDate
      })
      
      toast.success('Assignment rescheduled to tomorrow')
      loadAssignments()
    } catch (error) {
      toast.error('Failed to reschedule assignment')
    }
  }
  
  const handleCloseEditModal = () => {
    setEditingAssignment(null)
    setShowEditModal(false)
  }
  
  const handleSaveEdit = () => {
    loadData() // Reload data after saving
    handleCloseEditModal()
  }

  const getFilteredAssignments = (useHistoryFilter = false) => {
    let filtered = assignments.filter(a => {
      // Apply user filter for assignments view
      if (activeView === 'assignments' && manageUserFilter !== 'all') {
        if (a.assigned_to !== manageUserFilter) {
          return false
        }
      }

      // Apply date filter based on view
      const filterToUse = useHistoryFilter ? historyDateFilter : dateFilter
      const today = new Date()
      today.setHours(12, 0, 0, 0) // Use noon to match assignment dates

      if (filterToUse !== 'all' && activeView === 'assignments') {
        // Parse dates correctly - use timezone-aware extraction
        const dateStr = a.due_date ? extractDateInTimezoneSync(a.due_date) : extractDateInTimezoneSync(a.created_at)
        // Create date at noon to avoid timezone shifts
        const assignmentDate = new Date(dateStr + 'T12:00:00')
        
        switch (filterToUse) {
          case 'today':
            if (assignmentDate.toDateString() !== today.toDateString()) return false
            break
          case '2days':
            const twoDaysFromNow = new Date(today)
            twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2)
            if (assignmentDate < today || assignmentDate >= twoDaysFromNow) return false
            break
          case 'week':
            const weekFromNow = new Date(today)
            weekFromNow.setDate(weekFromNow.getDate() + 7)
            if (assignmentDate < today || assignmentDate >= weekFromNow) return false
            break
          case 'month':
            const monthFromNow = new Date(today)
            monthFromNow.setMonth(monthFromNow.getMonth() + 1)
            if (assignmentDate < today || assignmentDate >= monthFromNow) return false
            break
        }
      }
      
      if (filterToUse !== 'all' && activeView === 'history') {
        const completedDate = a.verified_at || a.completed_at || a.created_at
        const assignmentDate = new Date(completedDate)
        assignmentDate.setHours(0, 0, 0, 0)
        
        switch (filterToUse) {
          case 'today':
            if (assignmentDate.toDateString() !== today.toDateString()) return false
            break
          case '2days':
            const twoDaysAgo = new Date(today)
            twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)
            if (assignmentDate < twoDaysAgo || assignmentDate > today) return false
            break
          case 'week':
            const weekAgo = new Date(today)
            weekAgo.setDate(weekAgo.getDate() - 7)
            if (assignmentDate < weekAgo || assignmentDate > today) return false
            break
          case 'month':
            const monthAgo = new Date(today)
            monthAgo.setMonth(monthAgo.getMonth() - 1)
            if (assignmentDate < monthAgo || assignmentDate > today) return false
            break
        }
      }
      
      // Filter by child
      if (selectedChild !== 'all' && a.assigned_to !== selectedChild) {
        return false
      }

      // Filter by category
      if (selectedCategory !== 'all' && a.chore.category !== selectedCategory) {
        return false
      }

      // Filter by status
      if (statusFilter !== 'all') {
        if (statusFilter === 'overdue') {
          if (!a.due_date || ['completed', 'verified'].includes(a.status)) {
            return false
          }
          const isOverdue = isPast(parseISO(a.due_date))
          return isOverdue
        } else if (statusFilter === 'pending_verification') {
          return (a.status === 'completed' || a.status === 'pending_verification') && !a.verified_at
        } else {
          if (a.status !== statusFilter) {
            return false
          }
        }
      }

      // Filter by search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        return (
          a.chore.name.toLowerCase().includes(search) ||
          a.chore.description.toLowerCase().includes(search) ||
          (a.assigned_to_name && a.assigned_to_name.toLowerCase().includes(search))
        )
      }
      
      return true
    })
    
    // Apply sorting for assignments view
    if (activeView === 'assignments') {
      filtered.sort((a, b) => {
        let comparison = 0
        
        switch (manageSortBy) {
          case 'due_date':
            const dateA = a.due_date ? parseDueDate(a.due_date).getTime() : 0
            const dateB = b.due_date ? parseDueDate(b.due_date).getTime() : 0
            comparison = dateA - dateB
            break
          case 'created_date':
            const createdA = new Date(a.created_at).getTime()
            const createdB = new Date(b.created_at).getTime()
            comparison = createdA - createdB
            break
          case 'points':
            comparison = (a.points_offered || 0) - (b.points_offered || 0)
            break
          case 'user':
            comparison = (a.assigned_to_name || '').localeCompare(b.assigned_to_name || '')
            break
          case 'status':
            comparison = (a.status || '').localeCompare(b.status || '')
            break
        }
        
        return manageSortOrder === 'asc' ? comparison : -comparison
      })
    }
    
    return filtered
  }

  const getCategories = () => {
    const categories = new Set(chores.map(c => c.category))
    return Array.from(categories).sort()
  }

  const getAvailableChores = () => {
    // Get chores that don't have active assignments for today
    const assignedChoreIds = assignments
      .filter(a => {
        if (!a.due_date) return false
        const dueDateStr = extractDateInTimezoneSync(a.due_date)
        return dueDateStr === todayDateStr &&
               ['pending', 'in_progress', 'open'].includes(a.status)
      })
      .map(a => a.chore_id)
    
    // Return active chores that aren't already assigned today
    return chores.filter(c => 
      c.is_active && 
      !assignedChoreIds.includes(c.id) &&
      !c.rotation_group_id // Don't show rotation chores here
    )
  }

  const getFilteredChores = () => {
    let filtered = [...chores]
    
    // Apply search filter
    if (choreSearchTerm) {
      const search = choreSearchTerm.toLowerCase()
      filtered = filtered.filter(c => 
        c.name.toLowerCase().includes(search) ||
        c.description?.toLowerCase().includes(search) ||
        c.category.toLowerCase().includes(search)
      )
    }
    
    // Apply category filter
    if (choreCategoryFilter !== 'all') {
      filtered = filtered.filter(c => c.category === choreCategoryFilter)
    }
    
    // Apply difficulty filter
    if (choreDifficultyFilter !== 'all') {
      filtered = filtered.filter(c => c.difficulty === choreDifficultyFilter)
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (choreSortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'points':
          return b.base_points - a.base_points
        case 'time':
          return (a.estimated_minutes || 0) - (b.estimated_minutes || 0)
        case 'difficulty':
          const diffOrder = { easy: 1, medium: 2, hard: 3 }
          return (diffOrder[a.difficulty] || 0) - (diffOrder[b.difficulty] || 0)
        case 'category':
        default:
          return a.category.localeCompare(b.category) || a.name.localeCompare(b.name)
      }
    })
    
    return filtered
  }

  const groupChoresByCategory = (choresList: Chore[]) => {
    const grouped: { [key: string]: Chore[] } = {}
    choresList.forEach(chore => {
      if (!grouped[chore.category]) {
        grouped[chore.category] = []
      }
      grouped[chore.category].push(chore)
    })
    return grouped
  }

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories)
    if (newExpanded.has(category)) {
      newExpanded.delete(category)
    } else {
      newExpanded.add(category)
    }
    setExpandedCategories(newExpanded)
  }

  const formatDueDate = (dateString: string) => {
    const date = parseISO(dateString)
    const dueDateStr = format(date, 'yyyy-MM-dd')
    const hasTime = format(date, 'HH:mm') !== '00:00'

    let dateLabel = ''
    if (dueDateStr === todayDateStr) {
      dateLabel = 'Today'
    } else if (dueDateStr === tomorrowDateStr) {
      dateLabel = 'Tomorrow'
    } else {
      const days = differenceInDays(date, new Date())
      if (days < 0) {
        dateLabel = `${Math.abs(days)} days overdue`
      } else {
        dateLabel = format(date, 'MMM d')
      }
    }

    // Add time if not midnight
    if (hasTime) {
      dateLabel += ` ${format(date, 'h:mm a')}`
    }

    return dateLabel
  }

  const getStatusColor = (status: string, dueDate?: string) => {
    if (dueDate && isPast(parseISO(dueDate)) && !['completed', 'verified'].includes(status)) {
      return 'bg-red-100 text-red-800'
    }
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800'
      case 'pending': return 'bg-gray-100 text-gray-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'completed': return 'bg-purple-100 text-purple-800'
      case 'verified': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600'
      case 'medium': return 'text-yellow-600'
      case 'hard': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading dashboard...</span>
      </div>
    )
  }

  // Group navigation items by category
  const navGroups = [
    {
      title: 'Daily Operations',
      items: [
        { id: 'overview', icon: <Eye className="w-4 h-4" />, label: 'Overview', badge: 0 },
        { id: 'assignments', icon: <CheckCircle className="w-4 h-4" />, label: 'Assignments', badge: stats.pendingVerification + stats.overdue },
        { id: 'assign', icon: <Plus className="w-4 h-4" />, label: 'Assign Tasks', badge: 0 },
        { id: 'history', icon: <History className="w-4 h-4" />, label: 'History', badge: 0, highlight: assignments.filter(a => {
          // Highlight if verified in the last 24 hours
          if (a.status === 'verified' && a.verified_at) {
            const verifiedDate = new Date(a.verified_at);
            const now = new Date();
            const hoursDiff = (now.getTime() - verifiedDate.getTime()) / (1000 * 60 * 60);
            return hoursDiff <= 24;
          }
          return false;
        }).length > 0 },
      ]
    },
    {
      title: 'Planning & History',
      items: [
        { id: 'calendars', icon: <Calendar className="w-4 h-4" />, label: 'Calendars & Events', badge: 0 },
        { id: 'meals', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h18M3 7h18M3 11h18m-9 4h9" /></svg>, label: 'Meal Planning', badge: 0 },
        { id: 'shopping', icon: <ShoppingCart className="w-4 h-4" />, label: 'Shopping List', badge: 0 },
        { id: 'recipes', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>, label: 'Recipe Library', badge: 0 },
        { id: 'recipe-import', icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>, label: 'Import Recipe', badge: 0 },
      ]
    },
    {
      title: 'Communications',
      items: [
        { id: 'notifications', icon: <Bell className="w-4 h-4" />, label: 'Notifications', badge: 0 },
        { id: 'requests', icon: <MessageSquare className="w-4 h-4" />, label: 'Requests', badge: 0 },
      ]
    },
    {
      title: 'Reports & Analytics',
      items: [
        { id: 'redemption-queue', icon: <Gift className="w-4 h-4" />, label: 'Redemption Queue', badge: 0 },
        { id: 'weekly-summary', icon: <TrendingUp className="w-4 h-4" />, label: 'Weekly Summary', badge: 0 },
        { id: 'performance-trends', icon: <Archive className="w-4 h-4" />, label: 'Performance Trends', badge: 0 },
        // Greenlight Converter only for families with the greenlight toggle on
        ...(greenlightEnabled ? [{ id: 'greenlight-converter', icon: <CreditCard className="w-4 h-4" />, label: 'Greenlight Converter', badge: 0, highlight: true }] : []),
      ]
    },
    {
      title: 'Configuration',
      items: [
        { id: 'deductions', icon: <AlertTriangle className="w-4 h-4" />, label: 'Deductions', badge: 0 },
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex-1 text-center">
            <h1 className="text-lg font-bold text-gray-900">
              {getViewTitle()}
            </h1>
            <p className="text-xs text-gray-500">v{APP_VERSION}</p>
          </div>

          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
            {user?.display_name?.[0] || 'P'}
          </div>
        </div>

        {/* Dashboard Variant Toggle */}
        {activeView === 'overview' && (
          <div className="mt-3 flex items-center justify-center">
            <div className="inline-flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => {
                  setDashboardVariant('A')
                }}
                className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 bg-white text-blue-600 shadow-sm"
              >
                <LayoutGrid className="w-4 h-4" />
                Classic
              </button>
              <button
                onClick={() => {
                  setDashboardVariant('B')
                }}
                className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 text-gray-600"
              >
                <Grid className="w-4 h-4" />
                Tiles
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex h-full">
        {/* Sidebar Navigation - Mobile Responsive */}
        <div className={`
          fixed lg:relative
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
          bg-white border-r transition-all duration-300 flex-shrink-0
          h-full z-50 lg:z-auto
          top-0 left-0 bottom-0
        `}>
          <div className="p-4 h-full overflow-y-auto">
            {/* Mobile Close Button */}
            <div className="flex items-center justify-between mb-4 lg:hidden">
              <span className="font-bold text-lg">Menu</span>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Desktop Collapse Button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex w-full items-center justify-between mb-4"
            >
              {!sidebarCollapsed && <span className="font-bold text-lg">Menu</span>}
              <svg className={`w-5 h-5 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          
          <div className="space-y-6">
            {navGroups.map((group) => (
              <div key={group.title}>
                <h3 className={`text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 ${sidebarCollapsed ? 'hidden lg:hidden' : 'block'}`}>
                  {group.title}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.isExternal && item.path) {
                          window.location.href = item.path
                        } else {
                          setActiveView(item.id as ViewType)
                          setMobileMenuOpen(false) // Close menu on mobile after selection
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                        activeView === item.id
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      <div className="flex items-center">
                        {item.icon}
                        <span className={`ml-3 ${sidebarCollapsed ? 'hidden lg:hidden' : 'inline'}`}>{item.label}</span>
                      </div>
                      {item.badge > 0 && (
                        <span className={`${sidebarCollapsed ? 'absolute -top-1 -right-1' : ''} bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center`}>
                          {item.badge}
                        </span>
                      )}
                      {item.highlight && item.badge === 0 && (
                        <span className={`${sidebarCollapsed ? 'absolute -top-1 -right-1' : ''} bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5`}>
                          New
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Settings Link - Last item before logout */}
            <div className="pt-4 mt-4 border-t">
              <button
                onClick={() => navigate('/admin')}
                className="w-full flex items-center px-3 py-2 rounded-lg transition-all hover:bg-gray-100 text-gray-700 mb-2"
                title={sidebarCollapsed ? 'Settings' : undefined}
              >
                <Settings className="w-4 h-4" />
                {!sidebarCollapsed && <span className="ml-3">Settings</span>}
              </button>

              {/* PWA Install Button */}
              <PWAInstallButton
                className="w-full px-3 py-2 rounded-lg transition-all hover:bg-gray-100 text-gray-700 mb-2"
              />
            </div>
            
            {/* Logout Button */}
            <div className="pb-4">
              <button
                onClick={() => {
                  localStorage.removeItem('auth_token')
                  localStorage.removeItem('token')
                  localStorage.removeItem('user')
                  sessionStorage.clear()
                  toast.success('Logged out successfully')
                  navigate('/login')
                }}
                className="w-full flex items-center px-3 py-2 rounded-lg transition-all hover:bg-gray-100 text-gray-700"
                title={sidebarCollapsed ? 'Logout' : undefined}
              >
                <LogOut className="w-4 h-4" />
                {!sidebarCollapsed && <span className="ml-3">Logout</span>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        {/* Desktop Header - Hidden on Mobile */}
        <div className="hidden lg:block bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {getViewTitle()}
              </h1>

              {/* Dashboard Variant Toggle - Desktop */}
              {activeView === 'overview' && (
                <div className="inline-flex bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => {
                      setDashboardVariant('A')
                    }}
                    className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 bg-white text-blue-600 shadow-sm"
                  >
                    <LayoutGrid className="w-4 h-4" />
                    Classic
                  </button>
                  <button
                    onClick={() => {
                      setDashboardVariant('B')
                    }}
                    className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 text-gray-600"
                  >
                    <Grid className="w-4 h-4" />
                    Tiles
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                Welcome, {user?.display_name} • v{APP_VERSION}
              </span>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                {user?.display_name?.[0] || 'P'}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col h-full">
          {/* Priority Bar - Always visible at the top */}
          <PriorityBar 
            pendingVerifications={stats.pendingVerification}
            redemptionQueue={pendingRedemptions}
            availabilityRequests={pendingAvailability}
            overdueChores={stats.overdue}
            onNavigate={(section) => {
              if (section === 'overview') {
                setActiveView('overview')
              } else if (section === 'redemption-queue') {
                setActiveView('redemption-queue')
              } else if (section === 'assignments') {
                setActiveView('assignments')
                setActiveAssignmentTab('overdue')
              }
            }}
          />
          
          <div className="space-y-4 p-4 lg:p-6 flex-1 overflow-auto">{/* Content area */}


      {/* Filters (only for assignments view, not history) */}
      {activeView === 'assignments' && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search chores..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <select
              value={selectedChild}
              onChange={(e) => setSelectedChild(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">All Children</option>
              {children.map(child => (
                <option key={child.id} value={child.id}>
                  {child.display_name}
                </option>
              ))}
            </select>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">All Categories</option>
              {getCategories().map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_verification">Awaiting Verification</option>
              <option value="completed">Completed</option>
              <option value="verified">Verified</option>
              <option value="overdue">Overdue</option>
            </select>
            <button
              onClick={() => setGroupByCategory(!groupByCategory)}
              className={`p-2 rounded-lg transition-colors ${
                groupByCategory ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
              }`}
              title={groupByCategory ? 'Grouped view' : 'List view'}
            >
              {groupByCategory ? <Grid className="w-4 h-4" /> : <List className="w-4 h-4" />}
            </button>
            <button
              onClick={loadData}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Content Views */}
      {/* Overview View */}
      {activeView === 'overview' && dashboardMode === 'calendar' ? (
        <div className="space-y-4">
          <TodaysSummary
            children={children}
            assignments={assignments}
          />
          <UnifiedCalendarDashboard
            assignments={assignments}
            children={children}
            onVerifyAssignment={handleVerifyAssignment}
            onDeleteAssignment={handleDeleteAssignment}
            onReassignChore={handleReassignChore}
            onEventCreated={loadData}
          />
        </div>
      ) : activeView === 'overview' ? (
        <div className="space-y-6 p-4 lg:p-6">

            {/* Priority Quick Actions - Prominent Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Verify Pending Chores */}
              {stats.pendingVerification > 0 && (
                <button
                  onClick={() => setActiveView('assignments')}
                  className="group relative bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-6 h-6" />
                        <span className="text-sm font-medium opacity-90">Action Needed</span>
                      </div>
                      <div className="text-3xl font-bold mb-1">{stats.pendingVerification}</div>
                      <div className="text-sm opacity-90">Chores to Verify</div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 mt-2 opacity-75 group-hover:translate-x-1 transition-transform" />
                </button>
              )}

              {/* Assign New Tasks */}
              <button
                onClick={() => setActiveView('assign')}
                className="group bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 text-left"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Plus className="w-6 h-6" />
                      <span className="text-sm font-medium opacity-90">Quick Action</span>
                    </div>
                    <div className="text-2xl font-bold mb-1">Assign Tasks</div>
                    <div className="text-sm opacity-90">Create new assignments</div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 mt-2 opacity-75 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Process Redemptions */}
              {pendingRedemptions > 0 && (
                <button
                  onClick={() => setActiveView('redemption-queue')}
                  className="group relative bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Gift className="w-6 h-6" />
                        <span className="text-sm font-medium opacity-90">Pending</span>
                      </div>
                      <div className="text-3xl font-bold mb-1">{pendingRedemptions}</div>
                      <div className="text-sm opacity-90">Redemptions to Process</div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 mt-2 opacity-75 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>

            {/* Smart Insights Panel */}
            <SmartInsightsPanel
              children={children}
              assignments={assignments}
              onNavigate={setActiveView}
            />

            {/* Stats Overview - Enhanced */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Today's Overview
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => {
                    setActiveView('assignments')
                    setDateFilter('today')
                  }}
                  className="group bg-white rounded-xl shadow-sm p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{stats.todayAssigned}</div>
                  <div className="text-sm text-gray-600">Today's Tasks</div>
                </button>

                <button
                  onClick={() => {
                    setActiveView('history')
                    setDateFilter('today')
                  }}
                  className="group bg-white rounded-xl shadow-sm p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <div className="text-3xl font-bold text-green-600 mb-1">{stats.completedToday}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </button>

                <button
                  onClick={() => setActiveView('assign')}
                  className="group bg-white rounded-xl shadow-sm p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-yellow-50 rounded-lg">
                      <Target className="w-5 h-5 text-yellow-600" />
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-yellow-600 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <div className="text-3xl font-bold text-yellow-600 mb-1">{stats.openChores}</div>
                  <div className="text-sm text-gray-600">Open Bids</div>
                </button>

                <button
                  onClick={() => setActiveView('leaderboard')}
                  className="group bg-white rounded-xl shadow-sm p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-gray-100"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-teal-50 rounded-lg">
                      <Trophy className="w-5 h-5 text-teal-600" />
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all" />
                  </div>
                  <div className="text-3xl font-bold text-teal-600 mb-1">{stats.weeklyCompletion}%</div>
                  <div className="text-sm text-gray-600">Week Complete</div>
                </button>
              </div>
            </div>

            {/* Recent Activity & Kids Points */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                  <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    Recent Activity
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(() => {
                      // Deduplicate assignments by ID, keeping only the most recent state
                      const assignmentMap = new Map<string, typeof assignments[0]>()
                      assignments
                        .filter(a => a.completed_at || a.verified_at)
                        .forEach(a => {
                          const existing = assignmentMap.get(a.id)
                          if (!existing) {
                            assignmentMap.set(a.id, a)
                          } else {
                            // Keep the one with the most recent timestamp
                            const existingTime = new Date(existing.verified_at || existing.completed_at).getTime()
                            const newTime = new Date(a.verified_at || a.completed_at).getTime()
                            if (newTime > existingTime) {
                              assignmentMap.set(a.id, a)
                            }
                          }
                        })

                      return Array.from(assignmentMap.values())
                        .sort((a, b) => {
                          const dateA = new Date(a.verified_at || a.completed_at)
                          const dateB = new Date(b.verified_at || b.completed_at)
                          return dateB.getTime() - dateA.getTime()
                        })
                        .slice(0, 5)
                        .map(a => (
                        <div key={a.id} className="flex items-center justify-between text-sm p-3 hover:bg-gradient-to-r hover:from-gray-50 hover:to-white rounded-lg border border-transparent hover:border-gray-200 transition-all group">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${
                              a.status === 'verified' ? 'bg-green-100' :
                              a.status === 'completed' || a.status === 'pending_verification' ? 'bg-blue-100' :
                              'bg-gray-100'
                            }`}>
                              {a.status === 'verified' ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : a.status === 'completed' || a.status === 'pending_verification' ? (
                                <Clock className="w-4 h-4 text-blue-600" />
                              ) : (
                                <Target className="w-4 h-4 text-gray-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 truncate">{a.assigned_to_name}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(a.status, a.due_date)}`}>
                                  {a.status === 'verified' ? 'Verified' :
                                   a.status === 'pending_verification' ? 'Pending' :
                                   a.status === 'completed' ? 'Completed' : a.status}
                                </span>
                              </div>
                              <div className="text-gray-600 truncate">{a.chore.name}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {/* Quick action buttons */}
                            {(a.status === 'pending_verification' || a.status === 'completed') && (
                              <button
                                onClick={() => handleVerifyAssignment(a.id, true)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Verify completed"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            {(a.status === 'overdue' || a.status === 'pending') && (
                              <button
                                onClick={() => handleCompleteAssignment(a.id, a.assigned_to_name)}
                                className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Mark as completed"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                            )}
                            <span className="text-gray-500 text-xs whitespace-nowrap ml-2">
                              {format(parseISO(a.verified_at || a.completed_at || a.created_at), 'MMM d, h:mm a')}
                            </span>
                          </div>
                        </div>
                      ))
                    })()}
                  </div>
                </div>
              
              {/* Kids Points Summary */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  Kids Status & Points
                </h4>
                <div className="space-y-3">
                  {children.map(child => {
                    const childAssignments = assignments.filter(a => a.assigned_to === child.id)
                    const pendingCount = childAssignments.filter(a =>
                      ['pending', 'in_progress', 'pending_verification'].includes(a.status)
                    ).length
                    const completedToday = childAssignments.filter(a =>
                      a.status === 'verified' &&
                      a.verified_at &&
                      isToday(parseISO(a.verified_at))
                    ).length

                    return (
                      <div key={child.id} className="flex items-center justify-between p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-sm transition-shadow">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                            {child.display_name?.[0] || child.username[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{child.display_name}</div>
                            <div className="flex items-center gap-2 text-xs mt-1">
                              {pendingCount > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">
                                  <Clock className="w-3 h-3" />
                                  {pendingCount} pending
                                </span>
                              )}
                              {completedToday > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                                  <CheckCircle className="w-3 h-3" />
                                  {completedToday} today
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-baseline gap-1">
                            <Trophy className="w-4 h-4 text-yellow-500" />
                            <span className="font-bold text-2xl text-gray-900">{child.total_points}</span>
                          </div>
                          <div className="text-xs text-gray-500">total points</div>
                          {child.available_points !== undefined && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <div className="font-semibold text-lg text-green-600">{child.available_points}</div>
                              <div className="text-xs text-gray-500">available</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* School Events Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="p-2 bg-indigo-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                </div>
                School Events
              </h3>
              <SchoolEvents compact={true} />
            </div>
          </div>
        ) : null}

        {/* Create & Assign View */}
        {activeView === 'assign' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">Assign Tasks</h3>
                <p className="text-sm text-gray-600 mt-1">Create assignments from existing chores or add new one-time tasks</p>
              </div>
              <button
                onClick={() => setShowNewChoreForm(!showNewChoreForm)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                New Chore
              </button>
            </div>

            {showNewChoreForm && (
              <div className="border rounded-lg p-4 mb-4 bg-gray-50">
                <h4 className="font-medium mb-3">Create New Chore</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Chore Name
                    </label>
                    <input
                      type="text"
                      value={newChore.name}
                      onChange={(e) => setNewChore({...newChore, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Clean bathroom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={newChore.category}
                      onChange={(e) => setNewChore({...newChore, category: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="cleaning">Cleaning</option>
                      <option value="kitchen">Kitchen</option>
                      <option value="outdoor">Outdoor</option>
                      <option value="organizing">Organizing</option>
                      <option value="pets">Pets</option>
                      <option value="special">Special</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={newChore.description}
                      onChange={(e) => setNewChore({...newChore, description: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={2}
                      placeholder="Describe what needs to be done..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Points
                    </label>
                    <input
                      type="number"
                      value={newChore.base_points}
                      onChange={(e) => setNewChore({...newChore, base_points: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="1"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Minutes
                    </label>
                    <input
                      type="number"
                      value={newChore.estimated_minutes}
                      onChange={(e) => setNewChore({...newChore, estimated_minutes: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      min="5"
                      max="180"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Difficulty
                    </label>
                    <select
                      value={newChore.difficulty}
                      onChange={(e) => setNewChore({...newChore, difficulty: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assignment Type
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={!newChore.make_open}
                          onChange={() => setNewChore({...newChore, make_open: false})}
                          className="mr-2"
                        />
                        Assign to child
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={newChore.make_open}
                          onChange={() => setNewChore({...newChore, make_open: true, assigned_to: ''})}
                          className="mr-2"
                        />
                        Open for bidding
                      </label>
                    </div>
                  </div>
                  {!newChore.make_open && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assign To
                      </label>
                      <select
                        value={newChore.assigned_to}
                        onChange={(e) => setNewChore({...newChore, assigned_to: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">Select a child...</option>
                        {children.map(child => (
                          <option key={child.id} value={child.id}>
                            {child.display_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newChore.requires_verification}
                        onChange={(e) => setNewChore({...newChore, requires_verification: e.target.checked})}
                        className="mr-2"
                      />
                      Requires parent verification
                    </label>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    onClick={() => {
                      setShowNewChoreForm(false)
                      setNewChore({
                        name: '',
                        description: '',
                        category: 'cleaning',
                        base_points: 10,
                        estimated_minutes: 15,
                        difficulty: 'easy',
                        requires_verification: true,
                        make_open: false,
                        assigned_to: ''
                      })
                    }}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateChore}
                    disabled={!newChore.name || (!newChore.make_open && !newChore.assigned_to)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
                  >
                    <Save className="w-4 h-4 inline mr-2" />
                    Create Chore
                  </button>
                </div>
              </div>
            )}

            {/* Quick assign from existing chores */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Quick Assign from Library</h4>
              </div>
              
              {/* Assignment Controls */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search chores..."
                      value={assignSearchTerm}
                      onChange={(e) => setAssignSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </div>
                  <select
                    value={assignCategoryFilter}
                    onChange={(e) => setAssignCategoryFilter(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm bg-white"
                  >
                    <option value="">All Categories</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="kitchen">Kitchen</option>
                    <option value="outdoor">Outdoor</option>
                    <option value="organizing">Organizing</option>
                    <option value="pets">Pets</option>
                    <option value="special">Special</option>
                  </select>
                  <select
                    value={assignSelectedChild}
                    onChange={(e) => setAssignSelectedChild(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm bg-white"
                  >
                    <option value="">Open Assignment (Anyone)</option>
                    {children.map(child => (
                      <option key={child.id} value={child.id}>
                        {child.display_name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="date"
                    value={assignDueDate}
                    onChange={(e) => setAssignDueDate(e.target.value)}
                    className="px-3 py-2 border rounded-lg text-sm bg-white"
                    min={extractDateInTimezoneSync(new Date().toISOString())}
                  />
                </div>
                <div className="mt-2 text-xs text-blue-700">
                  Tip: Select a child and date above, then click the + button next to any chore to assign it
                </div>
              </div>
              
              {/* Filtered chores count */}
              {(assignSearchTerm || assignCategoryFilter) && (
                <div className="text-sm text-gray-600">
                  Showing {
                    chores
                      .filter(c => c.is_active)
                      .filter(c => !assignSearchTerm || c.name.toLowerCase().includes(assignSearchTerm.toLowerCase()))
                      .filter(c => !assignCategoryFilter || c.category === assignCategoryFilter)
                      .length
                  } chores
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto">
                {chores
                  .filter(c => c.is_active)
                  .filter(c => !assignSearchTerm || c.name.toLowerCase().includes(assignSearchTerm.toLowerCase()))
                  .filter(c => !assignCategoryFilter || c.category === assignCategoryFilter)
                  .sort((a, b) => {
                    // Sort by category first
                    if (a.category !== b.category) {
                      // Put 'money' category first (Greenlight chores)
                      if (a.category === 'money') return -1
                      if (b.category === 'money') return 1
                      return a.category.localeCompare(b.category)
                    }
                    // Within same category, sort by points (lowest to highest for money category)
                    if (a.category === 'money') {
                      return a.base_points - b.base_points
                    }
                    // For other categories, sort by name
                    return a.name.localeCompare(b.name)
                  })
                  .map(chore => (
                    <div key={chore.id} className="border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h5 className="font-medium text-sm">{chore.name}</h5>
                          <div className="text-xs text-gray-500 mt-1">
                            <span className="inline-block px-2 py-0.5 bg-gray-100 rounded-full">
                              {chore.category}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                            <span className={getDifficultyColor(chore.difficulty)}>
                              {chore.difficulty}
                            </span>
                            <span>•</span>
                            <span>{chore.base_points} pts</span>
                            <span>•</span>
                            <span>{chore.estimated_minutes} min</span>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await apiClient.post('/api/assignments/', {
                                chore_id: chore.id,
                                assigned_to: assignSelectedChild || null,
                                points_offered: chore.base_points,
                                due_date: assignDueDate
                              })
                              
                              const assigneeName = assignSelectedChild 
                                ? children.find(c => c.id === assignSelectedChild)?.display_name 
                                : 'Open'
                              
                              toast.success(
                                `${chore.name} assigned to ${assigneeName} for ${assignDueDate}`
                              )
                              loadData()
                            } catch (error: any) {
                              console.error('Assignment creation failed:', error)
                              const errorMsg = error.response?.data?.detail || error.message || 'Failed to create assignment'
                              toast.error(errorMsg)
                            }
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title={`Assign to ${assignSelectedChild ? children.find(c => c.id === assignSelectedChild)?.display_name : 'Open'} for ${assignDueDate}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              
              {/* Show message if no chores match filters */}
              {chores
                .filter(c => c.is_active)
                .filter(c => !assignSearchTerm || c.name.toLowerCase().includes(assignSearchTerm.toLowerCase()))
                .filter(c => !assignCategoryFilter || c.category === assignCategoryFilter)
                .length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No chores found matching your filters
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assignments View */}
        {activeView === 'assignments' && (
          <div className="p-6">
            {/* Tab Navigation */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setActiveAssignmentTab('active')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    activeAssignmentTab === 'active'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Active ({assignments.filter(a => a.status !== 'completed' && a.status !== 'verified' && !isOverdue(a.due_date)).length})
                </button>
                <button
                  onClick={() => setActiveAssignmentTab('verify')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    activeAssignmentTab === 'verify'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Verify
                </button>
                <button
                  onClick={() => setActiveAssignmentTab('overdue')}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    activeAssignmentTab === 'overdue'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Overdue ({assignments.filter(a => a.status !== 'completed' && a.status !== 'verified' && a.due_date && isOverdue(a.due_date)).length})
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeAssignmentTab === 'verify' && (
              <EnhancedChoreVerification onVerificationComplete={() => loadData(true)} />
            )}

            {(activeAssignmentTab === 'active' || activeAssignmentTab === 'overdue') && (
              <div>
                {/* Alert section for overdue tab */}
                {activeAssignmentTab === 'overdue' && (() => {
                  const overdueAssignments = assignments.filter(a => 
                    a.status !== 'completed' && a.status !== 'verified' && 
                    a.due_date && isOverdue(a.due_date)
                  )
                  
                  if (overdueAssignments.length > 0) {
                    return (
                      <div className="mb-6">
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <h4 className="font-semibold text-red-900 mb-3 flex items-center">
                            <AlertCircle className="w-5 h-5 mr-2" />
                            Overdue Assignments ({overdueAssignments.length})
                          </h4>
                          <div className="space-y-2">
                            {overdueAssignments.map(assignment => (
                              <div key={assignment.id} className="bg-white p-3 rounded border border-red-200">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-medium">{assignment.chore.name}</span>
                                      <span className="text-sm text-gray-600">→ {assignment.assigned_to_name || 'Unassigned'}</span>
                                      <span className="text-xs text-red-600">
                                        Due {format(parseISO(assignment.due_date), 'MMM d')}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => handleEditAssignment(assignment)}
                                      className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleQuickReschedule(assignment.id)}
                                      className="px-3 py-1 bg-orange-500 text-white text-sm rounded hover:bg-orange-600"
                                    >
                                      Reschedule
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  }
                  return null
                })()}

                {/* Header with Date Filters */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">
                    {activeAssignmentTab === 'active' ? 'Active Assignments' : 'Overdue Assignments'}
                  </h3>
                  
                  {/* Date Filter Buttons */}
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                      <button
                        onClick={() => setDateFilter('today')}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          dateFilter === 'today' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Today
                      </button>
                      <button
                        onClick={() => setDateFilter('week')}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          dateFilter === 'week' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Week
                      </button>
                      <button
                        onClick={() => setDateFilter('month')}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          dateFilter === 'month' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Month
                      </button>
                      <button
                        onClick={() => setDateFilter('all')}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          dateFilter === 'all' 
                            ? 'bg-white text-blue-600 shadow-sm' 
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        All
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Sorting and User Filter Controls */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-gray-600">
                      Showing {(() => {
                        if (activeAssignmentTab === 'active') {
                          return assignments.filter(a => a.status !== 'completed' && a.status !== 'verified' && !isOverdue(a.due_date)).length
                        } else {
                          return assignments.filter(a => a.status !== 'completed' && a.status !== 'verified' && a.due_date && isOverdue(a.due_date)).length
                        }
                      })()} assignments
                    </div>
                    
                    {/* User Filter */}
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <select
                        value={manageUserFilter}
                        onChange={(e) => setManageUserFilter(e.target.value)}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Kids</option>
                        {children.map(kid => (
                          <option key={kid.id} value={kid.id}>
                            {kid.display_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Sort by:</span>
                    <select
                      value={manageSortBy}
                      onChange={(e) => setManageSortBy(e.target.value as any)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="due_date">Due Date</option>
                      <option value="created_date">Created Date</option>
                      <option value="points">Points</option>
                      <option value="user">User</option>
                      <option value="status">Status</option>
                    </select>
                    <button
                      onClick={() => setManageSortOrder(manageSortOrder === 'asc' ? 'desc' : 'asc')}
                      className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                      title={`Sort ${manageSortOrder === 'asc' ? 'descending' : 'ascending'}`}
                    >
                      <ArrowUpDown className="w-4 h-4" />
                      {manageSortOrder === 'desc' ? '↓' : '↑'}
                    </button>
                  </div>
                </div>
                
                {/* Assignment List Rendering */}
                {(() => {
                  // Filter assignments based on active tab
                  let filteredAssignments = assignments.filter(a => {
                    // Apply user filter
                    if (manageUserFilter !== 'all' && a.assigned_to !== manageUserFilter) {
                      return false
                    }
                    
                    // Apply tab-specific filtering
                    if (activeAssignmentTab === 'active') {
                      return a.status !== 'completed' && a.status !== 'verified' && (!a.due_date || !isOverdue(a.due_date))
                    } else if (activeAssignmentTab === 'verify') {
                      return a.status === 'completed' && !a.verified_at
                    } else if (activeAssignmentTab === 'overdue') {
                      return a.status !== 'completed' && a.status !== 'verified' && a.due_date && isOverdue(a.due_date)
                    }
                    return false
                  })
                  
                  // Apply date filtering
                  const today = new Date()
                  today.setHours(12, 0, 0, 0) // Use noon to match assignment dates

                  if (dateFilter !== 'all') {
                    filteredAssignments = filteredAssignments.filter(a => {
                      // Use timezone-aware extraction
                      const dateStr = a.due_date ? extractDateInTimezoneSync(a.due_date) : extractDateInTimezoneSync(a.created_at)
                      // Create date at noon to avoid timezone shifts
                      const assignmentDate = new Date(dateStr + 'T12:00:00')
                      
                      if (dateFilter === 'today') {
                        return assignmentDate.toDateString() === today.toDateString()
                      } else if (dateFilter === '2days') {
                        const twoDaysFromNow = new Date(today)
                        twoDaysFromNow.setDate(today.getDate() + 2)
                        return assignmentDate.getTime() <= twoDaysFromNow.getTime()
                      } else if (dateFilter === 'week') {
                        const oneWeekFromNow = new Date(today)
                        oneWeekFromNow.setDate(today.getDate() + 7)
                        return assignmentDate.getTime() <= oneWeekFromNow.getTime()
                      } else if (dateFilter === 'month') {
                        const oneMonthFromNow = new Date(today)
                        oneMonthFromNow.setMonth(today.getMonth() + 1)
                        return assignmentDate.getTime() <= oneMonthFromNow.getTime()
                      }
                      return true
                    })
                  }
                  
                  // Apply sorting
                  filteredAssignments.sort((a, b) => {
                    let comparison = 0
                    
                    switch (manageSortBy) {
                      case 'due_date':
                        const dueDateA = a.due_date ? parseDueDate(a.due_date).getTime() : 0
                        const dueDateB = b.due_date ? parseDueDate(b.due_date).getTime() : 0
                        comparison = dueDateA - dueDateB
                        break
                      case 'created_date':
                        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                        break
                      case 'points':
                        comparison = a.points_offered - b.points_offered
                        break
                      case 'user':
                        comparison = (a.assigned_to_name || '').localeCompare(b.assigned_to_name || '')
                        break
                      case 'status':
                        comparison = a.status.localeCompare(b.status)
                        break
                    }
                    
                    return manageSortOrder === 'desc' ? -comparison : comparison
                  })
                  
                  if (filteredAssignments.length === 0) {
                    return (
                      <div className="text-center py-8 text-gray-500">
                        No {activeAssignmentTab} assignments match your filters
                      </div>
                    )
                  }
                  
                  return (
                    <div className="space-y-2">
                      {filteredAssignments.map(assignment => {
                        const assignedUser = children.find(u => u.id === assignment.assigned_to)
                        return (
                          <div key={assignment.id} className="border rounded-lg p-3 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(assignment.status, assignment.due_date)}`}>
                                    {assignment.status.replace('_', ' ')}
                                  </span>
                                  <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">{assignment.chore.name}</h3>
                                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                                      <span>by {assignedUser ? assignedUser.display_name : 'Unassigned'}</span>
                                      {assignment.due_date && (
                                        <span>Due: {format(parseISO(assignment.due_date), 'MMM d, yyyy')}</span>
                                      )}
                                      <span>{assignment.points_offered} points</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditAssignment(assignment)}
                                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                                >
                                  Edit
                                </button>
                                {assignment.status === 'completed' && !assignment.verified_at && (
                                  <button
                                    onClick={() => {
                                      setActiveAssignmentTab('verify')
                                    }}
                                    className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600"
                                  >
                                    Verify
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </div>
            )}
          </div>
        )}


        {/* History View */}
        {activeView === 'history' && (
          <div className="p-6">
            <AssignmentHistory />
          </div>
        )}


        {/* Deductions Management View */}
        {activeView === 'deductions' && (
          <div className="p-6">
            <EnhancedDeductionsManager />
          </div>
        )}

        {/* Recipes Management View */}
        {activeView === 'recipes' && (
          <div className="p-6">
            <RecipeList />
          </div>
        )}

        {/* Requests & Availability View */}
        {activeView === 'requests' && (
          <div className="p-6">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold mb-4">Kids' Requests & Availability</h2>
                <AvailabilityApprovals />
              </div>
            </div>
          </div>
        )}

        {/* Shopping List View */}
        {activeView === 'shopping' && (
          <div className="p-6">
            <ShoppingListGenerator />
          </div>
        )}

        {/* School Calendars View */}
        {activeView === 'calendars' && (
          <div className="p-6">
            <FamilyScheduleFixed initialTab="calendar" />
          </div>
        )}

        {/* School & Transport Management */}
        {activeView === 'school-management' && (
          <div className="p-6">
            <ParentSchoolManagement />
          </div>
        )}

        {/* Recipe Import View */}
        {activeView === 'recipe-import' && (
          <div className="p-6">
            <RecipeImporter />
          </div>
        )}

        {/* Meal Planning View */}
        {activeView === 'meals' && (
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4">Meal Planning</h2>
            <EnhancedMealPlanEditor />
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Shopping List</h3>
              <ShoppingListGenerator />
            </div>
          </div>
        )}

        {/* Notifications View */}
        {activeView === 'notifications' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Notification Center */}
              <div className="lg:col-span-2">
                <NotificationCenter />
              </div>
              
              {/* Notification Settings */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                  <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full text-left px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                      Send Test Notification
                    </button>
                    <button className="w-full text-left px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100">
                      Clear All Notifications
                    </button>
                    <button className="w-full text-left px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100">
                      View Notification History
                    </button>
                  </div>
                </div>
                
                <NotificationSettings />
              </div>
            </div>
          </div>
        )}


        {/* Redemption Queue View */}
        {activeView === 'redemption-queue' && (
          <div className="p-6">
            <RedemptionQueue />
          </div>
        )}

        {/* Weekly Summary View */}
        {activeView === 'weekly-summary' && (
          <div className="p-6">
            <WeeklySummary />
          </div>
        )}

        {/* Performance Trends View */}
        {activeView === 'performance-trends' && (
          <div className="p-6">
            <PerformanceTrends />
          </div>
        )}

        {/* Greenlight Converter View */}
        {activeView === 'greenlight-converter' && (
          <div className="p-6">
            <GreenlightConverter />
          </div>
        )}
          </div>
        </div>
      </div>
    </div>

    {/* Edit Assignment Modal */}
    {editingAssignment && (
      <ChoreAssignmentEditModal
        assignment={editingAssignment}
        children={children}
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdit}
      />
    )}
    
    {/* Version Footer */}
    <div className="mt-8 text-center text-xs text-gray-400">
      v{APP_VERSION}
    </div>

    {/* PWA Install Prompt */}
    <PWAInstallPrompt />
  </div>
  )
}// Cache bust: Wed Sep 10 19:31:32 EDT 2025
